import type React from "react";
import {useEffect, useRef, useState} from "react";

import type {ThreeEvent} from "@react-three/fiber";
import * as THREE from "three";

import type {InstanceManager} from "./InstanceManager";

interface ManagedInstancingProps {
  instanceManager: InstanceManager;
  minInstanceCount?: number;
  onClick?: ((event: ThreeEvent<MouseEvent>) => void) | undefined;
  children: React.ReactNode;
  renderSelection?: boolean;
}

const invisibleMatrix = new THREE.Matrix4();
invisibleMatrix.setPosition(0, 0, 1000);

export function ManagedInstancing({
  instanceManager,
  minInstanceCount = 500, //TODO: lower
  onClick,
  children,
  renderSelection = false,
}: ManagedInstancingProps) {
  const [instanceLimit, setInstanceLimit] = useState(() =>
    Math.max(minInstanceCount, instanceManager.objects.length),
  );

  const instancedMeshRef = useRef<THREE.InstancedMesh>(null!);
  const variantAttributeRef = useRef<THREE.InstancedBufferAttribute>(null!);

  useEffect(() => {
    const mesh = instancedMeshRef.current;

    // Infinite bounding box & bounding sphere
    mesh.boundingBox = new THREE.Box3();
    mesh.boundingBox.min.set(
      Number.NEGATIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
      Number.NEGATIVE_INFINITY,
    );
    mesh.boundingBox.max.set(
      Number.POSITIVE_INFINITY,
      Number.POSITIVE_INFINITY,
      Number.POSITIVE_INFINITY,
    );

    mesh.boundingSphere = new THREE.Sphere();
    mesh.boundingSphere.radius = Number.POSITIVE_INFINITY;

    /**
     * Make sure we have a variantAttribute sized to 'instanceLimit'.
     * This attribute stores 1 float32 per instance (the "variant" value).
     * If we already have one, but it's not big enough, replace it.
     */
    function ensureVariantAttributeSize() {
      if (
        !variantAttributeRef.current ||
        variantAttributeRef.current.count < instanceLimit
      ) {
        // Create new attribute with 'instanceLimit' float32
        // Each item has 1 component (the variant).
        variantAttributeRef.current = new THREE.InstancedBufferAttribute(
          new Float32Array(instanceLimit),
          1,
        );

        // Attach it to the geometry
        const geometry = mesh.geometry as THREE.InstancedBufferGeometry;
        geometry.setAttribute("variant", variantAttributeRef.current);
      }
    }

    ensureVariantAttributeSize();

    /**
     * handleInstanceCount:
     * - If the InstanceManager has more objects than our current limit, expand limit (usually doubling).
     * - Update 'mesh.count' to the actual number of objects we're using.
     */
    function handleInstanceCount() {
      // Check if we need to increase the instance limit
      if (instanceManager.objects.length > instanceLimit) {
        let newLimit = instanceLimit;
        while (instanceManager.objects.length > newLimit) {
          newLimit *= 2;
        }
        setInstanceLimit(newLimit);
      }

      if (mesh.count !== instanceManager.objects.length) {
        mesh.count = instanceManager.objects.length;
      }
    }

    /**
     * updateInstanceMatrices:
     * - Called initially to set all the matrices according to
     *   either the real object matrix or the invisibleMatrix (if renderSelection).
     * - Also updates the 'variant' attribute for each instance.
     */
    function updateInstanceMatrices() {
      if (!variantAttributeRef.current) return;
      instanceManager.objects.forEach((object, index) => {
        // matrix
        if (renderSelection) {
          mesh.setMatrixAt(index, invisibleMatrix);
        } else {
          mesh.setMatrixAt(index, object.matrix);
        }

        // variant
        const variantValue = object.userData["variant"] ?? 0;
        variantAttributeRef.current!.setX(index, variantValue);
      });

      variantAttributeRef.current.needsUpdate = true;
      mesh.instanceMatrix.needsUpdate = true;
    }

    /**
     * handleInstanceChange:
     * - Called whenever a particular instance changes in the InstanceManager
     * - We'll update that instance's matrix and variant in the attribute
     */
    function handleInstanceChange(index: number) {
      handleInstanceCount();
      ensureVariantAttributeSize();

      if (index < instanceManager.objects.length) {
        // update matrix
        const object = instanceManager.objects[index]!;
        if (renderSelection && !instanceManager.isSelected(index)) {
          mesh.setMatrixAt(index, invisibleMatrix);
        } else {
          mesh.setMatrixAt(index, object.matrix);
        }

        // update variant
        if (variantAttributeRef.current) {
          const variantValue = object.userData["variant"] ?? 0;
          variantAttributeRef.current.setX(index, variantValue);
          variantAttributeRef.current.needsUpdate = true;
        }

        mesh.instanceMatrix.needsUpdate = true;
      }
    }

    // Run once on mount:
    handleInstanceCount();
    updateInstanceMatrices();

    // Return the unsubscriber from the instanceManager
    return instanceManager.registerChangeCallback(handleInstanceChange);
  }, [instanceManager, instanceLimit, renderSelection]);

  return (
    <instancedMesh
      ref={instancedMeshRef}
      args={[undefined, undefined, instanceLimit]}
      frustumCulled={false}
      onClick={onClick}
    >
      {children}
    </instancedMesh>
  );
}
